// web/src/pages/ResetPassword.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, Link } from "react-router-dom";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
  const qs = useQuery();
  const navigate = useNavigate();
  const token = qs.get("token") || "";
  const [status, setStatus] = useState<"loading"|"invalid"|"ready"|"submitting"|"done">("loading");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function validate() {
      if (!token) { setStatus("invalid"); return; }
      try {
        const r = await fetch(`/api/auth/reset/validate?token=${encodeURIComponent(token)}`, { credentials: "include" });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (mounted) { setEmail(j.email || ""); setStatus("ready"); }
      } catch {
        if (mounted) setStatus("invalid");
      }
    }
    validate();
    return () => { mounted = false; };
  }, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) { setError("Mot de passe trop court (min. 8 caractères)."); return; }
    if (pwd !== pwd2) { setError("Les mots de passe ne correspondent pas."); return; }

    setStatus("submitting");
    try {
      const r = await fetch("/api/auth/reset/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pwd }),
      });
      const j = await r.json().catch(()=> ({}));
      if (!r.ok) {
        setStatus("ready");
        setError(j?.detail || "Impossible de réinitialiser le mot de passe.");
        return;
      }
      setStatus("done");
      // Server sets a fresh session cookie. Redirect to /compte.
      const target = j?.redirect || "/compte";
      setTimeout(() => navigate(target, { replace: true }), 300);
    } catch (e) {
      setStatus("ready");
      setError("Erreur réseau. Veuillez réessayer.");
    }
  }

  return (
    <div style={{ minHeight:"100vh", display:"grid", placeItems:"center", background:"#f8fafc", padding:"20px" }}>
      <Helmet>
        <title>Réinitialiser le mot de passe – État du Lieu</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={{ width:"100%", maxWidth:420, background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:20, boxShadow:"0 10px 30px rgba(0,0,0,.06)" }}>
        {status === "loading" && (
          <>
            <h1 style={{ marginTop:0 }}>Vérification…</h1>
            <p>Veuillez patienter.</p>
          </>
        )}

        {status === "invalid" && (
          <>
            <h1 style={{ marginTop:0 }}>Lien invalide</h1>
            <p>Le lien de réinitialisation est invalide ou a expiré.</p>
            <p><Link to="/auth/reset">Demander un nouveau lien</Link></p>
          </>
        )}

        {(status === "ready" || status === "submitting") && (
          <>
            <h1 style={{ marginTop:0 }}>Nouveau mot de passe</h1>
            {email && <p style={{ color:"#6b7280", fontSize:14, marginTop:-6, marginBottom:12 }}>Pour : <strong>{email}</strong></p>}
            <form onSubmit={onSubmit}>
              <label style={{ display:"block", fontWeight:600, marginTop:8 }}>Mot de passe</label>
              <input
                type="password"
                required
                value={pwd}
                onChange={e=>setPwd(e.target.value)}
                minLength={8}
                style={{ width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", marginTop:6 }}
              />
              <label style={{ display:"block", fontWeight:600, marginTop:12 }}>Confirmer le mot de passe</label>
              <input
                type="password"
                required
                value={pwd2}
                onChange={e=>setPwd2(e.target.value)}
                minLength={8}
                style={{ width:"100%", padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", marginTop:6 }}
              />
              {error && <div style={{ marginTop:12, color:"#b91c1c", background:"#fee2e2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 12px" }}>{error}</div>}
              <button
                type="submit"
                disabled={status === "submitting"}
                style={{ marginTop:16, width:"100%", padding:"12px 14px", borderRadius:12, border:"none", background:"#111827", color:"#fff", fontWeight:800 }}
              >
                {status === "submitting" ? "Mise à jour…" : "Mettre à jour le mot de passe"}
              </button>
            </form>
          </>
        )}

        {status === "done" && (
          <>
            <h1 style={{ marginTop:0 }}>Mot de passe mis à jour ✅</h1>
            <p>Redirection vers votre compte…</p>
          </>
        )}
      </div>
    </div>
  );
}
